/**
 * Macro Helper Functions
 * Utilities for generating, validating, and previewing MomentScience macros
 */

import { Macro, MacroPattern, MacroTestData, MacroValidation, MacroSession, MacroTemplate } from '../types/macros'

/**
 * Generate macro syntax string from macro configuration
 */
export const generateMacroSyntax = (macro: Macro): string => {
  // If it's an advanced pattern with custom syntax, return that
  if (macro.pattern === MacroPattern.ADVANCED && macro.customSyntax) {
    return macro.customSyntax
  }
  
  switch (macro.pattern) {
    case MacroPattern.SIMPLE:
      return `{{${macro.key}}}`
    
    case MacroPattern.FALLBACK:
      return `{{${macro.key}|'${macro.fallbackValue || ''}'}}` 
    
    case MacroPattern.CONDITIONAL:
      return `{{#if ${macro.key}}}${macro.conditionalContent || ''}{{/if}}`
    
    case MacroPattern.CONDITIONAL_ELSE:
      return `{{#if ${macro.key}}}${macro.conditionalContent || ''}{{else}}${macro.elseContent || ''}{{/if}}`
    
    default:
      return ''
  }
}

/**
 * Validate macro key format
 */
export const validateMacroKey = (key: string): MacroValidation => {
  const errors: string[] = []
  
  if (!key || key.trim() === '') {
    errors.push('Macro key cannot be empty')
  }
  
  if (key.includes(' ')) {
    errors.push('Macro key cannot contain spaces')
  }
  
  if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(key)) {
    errors.push('Macro key must start with a letter or underscore and contain only alphanumeric characters and underscores')
  }
  
  if (key.length > 50) {
    errors.push('Macro key must be 50 characters or less')
  }
  
  return {
    isValid: errors.length === 0,
    errors
  }
}

/**
 * Validate custom macro syntax
 */
export const validateMacroSyntax = (syntax: string): MacroValidation => {
  const errors: string[] = []
  
  if (!syntax || syntax.trim() === '') {
    errors.push('Macro syntax cannot be empty')
  }
  
  // Check for balanced curly braces
  const openBraces = (syntax.match(/{{/g) || []).length
  const closeBraces = (syntax.match(/}}/g) || []).length
  if (openBraces !== closeBraces) {
    errors.push('Unbalanced {{ }} - make sure every opening has a closing')
  }
  
  // Check for balanced #if/#endif
  const ifCount = (syntax.match(/{{#if\s+\w+}}/g) || []).length
  const endifCount = (syntax.match(/{{\s*\/if\s*}}/g) || []).length
  if (ifCount !== endifCount) {
    errors.push('Unbalanced {{#if}} / {{/if}} - every #if needs a closing /if')
  }
  
  // Check for valid key names in simple replacements
  const simpleKeys = syntax.match(/{{(?!#if|#else|\/if)(\w+)(\|[^}]+)?}}/g)
  if (simpleKeys) {
    simpleKeys.forEach(match => {
      const keyMatch = match.match(/{{(\w+)/)
      if (keyMatch && !/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(keyMatch[1])) {
        errors.push(`Invalid key name: ${keyMatch[1]}`)
      }
    })
  }
  
  return {
    isValid: errors.length === 0,
    errors
  }
}

/**
 * Extract all macro keys from a syntax string
 */
export const extractKeysFromSyntax = (syntax: string): string[] => {
  const keys: Set<string> = new Set()
  
  // Match {{key}}, {{key|'fallback'}}, and {{#if key}}
  const patterns = [
    /{{(?!#if|#else|\/if|else)(\w+)/g,  // Simple and fallback
    /{{#if\s+(\w+)}}/g                 // Conditional
  ]
  
  patterns.forEach(pattern => {
    let match
    while ((match = pattern.exec(syntax)) !== null) {
      keys.add(match[1])
    }
  })
  
  return Array.from(keys)
}

/**
 * Preview macro output with test data
 */
export const previewMacroWithData = (macro: Macro, testData: MacroTestData): string => {
  // Handle advanced patterns with custom syntax
  if (macro.pattern === MacroPattern.ADVANCED && macro.customSyntax) {
    return renderMacroSyntax(macro.customSyntax, testData)
  }
  
  const value = testData[macro.key]
  
  switch (macro.pattern) {
    case MacroPattern.SIMPLE:
      return value || ''
    
    case MacroPattern.FALLBACK:
      return value || macro.fallbackValue || ''
    
    case MacroPattern.CONDITIONAL:
      return value ? renderMacroSyntax(macro.conditionalContent || '', testData) : ''
    
    case MacroPattern.CONDITIONAL_ELSE:
      return value ? renderMacroSyntax(macro.conditionalContent || '', testData) : renderMacroSyntax(macro.elseContent || '', testData)
    
    default:
      return ''
  }
}

/**
 * Preview macro output without test data (empty scenario)
 */
export const previewMacroWithoutData = (macro: Macro): string => {
  // Handle advanced patterns with custom syntax
  if (macro.pattern === MacroPattern.ADVANCED && macro.customSyntax) {
    return renderMacroSyntax(macro.customSyntax, {})
  }
  
  switch (macro.pattern) {
    case MacroPattern.SIMPLE:
      return ''
    
    case MacroPattern.FALLBACK:
      return macro.fallbackValue || ''
    
    case MacroPattern.CONDITIONAL:
      return ''
    
    case MacroPattern.CONDITIONAL_ELSE:
      return renderMacroSyntax(macro.elseContent || '', {})
    
    default:
      return ''
  }
}

/**
 * Render macro syntax with test data (simple parser)
 */
const renderMacroSyntax = (syntax: string, testData: MacroTestData): string => {
  let result = syntax
  
  // Handle conditionals {{#if key}}...{{else}}...{{/if}}
  const conditionalRegex = /{{#if\s+(\w+)}}([\s\S]*?)(?:{{else}}([\s\S]*?))?{{\/if}}/g
  result = result.replace(conditionalRegex, (_match, key, ifContent, elseContent) => {
    if (testData[key]) {
      return renderMacroSyntax(ifContent, testData)
    } else {
      return elseContent ? renderMacroSyntax(elseContent, testData) : ''
    }
  })
  
  // Handle fallback {{key|'default'}}
  const fallbackRegex = /{{(\w+)\|'([^']*)''}}/g
  result = result.replace(fallbackRegex, (_match, key, fallback) => {
    return testData[key] || fallback
  })
  
  // Handle simple replacements {{key}}
  const simpleRegex = /{{(\w+)}}/g
  result = result.replace(simpleRegex, (_match, key) => {
    return testData[key] || ''
  })
  
  return result
}

/**
 * Generate SDK payload code with all macro keys
 */
export const generateSDKPayload = (macros: Macro[], testData: MacroTestData): string => {
  // Extract all keys including from advanced patterns
  const allKeys = new Set<string>()
  
  macros.forEach(macro => {
    if (macro.pattern === MacroPattern.ADVANCED && macro.customSyntax) {
      extractKeysFromSyntax(macro.customSyntax).forEach(key => allKeys.add(key))
    } else {
      allKeys.add(macro.key)
      // Extract nested keys from content
      if (macro.conditionalContent) {
        extractKeysFromSyntax(macro.conditionalContent).forEach(key => allKeys.add(key))
      }
      if (macro.elseContent) {
        extractKeysFromSyntax(macro.elseContent).forEach(key => allKeys.add(key))
      }
    }
  })
  
  const uniqueKeys = Array.from(allKeys)
  
  const payloadObject: Record<string, string> = {}
  uniqueKeys.forEach(key => {
    payloadObject[key] = testData[key] || `'your_${key}_here'`
  })
  
  const payloadLines = Object.entries(payloadObject)
    .map(([key, value]) => `  ${key}: ${value.startsWith("'") ? value : `"${value}"`}`)
    .join(',\n')
  
  return `window.AdpxUser = {
${payloadLines}
};`
}

/**
 * Generate URL parameter string
 */
export const generateURLParams = (macros: Macro[], testData: MacroTestData): string => {
  // Extract all keys including from advanced patterns
  const allKeys = new Set<string>()
  
  macros.forEach(macro => {
    if (macro.pattern === MacroPattern.ADVANCED && macro.customSyntax) {
      extractKeysFromSyntax(macro.customSyntax).forEach(key => allKeys.add(key))
    } else {
      allKeys.add(macro.key)
      // Extract nested keys from content
      if (macro.conditionalContent) {
        extractKeysFromSyntax(macro.conditionalContent).forEach(key => allKeys.add(key))
      }
      if (macro.elseContent) {
        extractKeysFromSyntax(macro.elseContent).forEach(key => allKeys.add(key))
      }
    }
  })
  
  const uniqueKeys = Array.from(allKeys)
  
  const params = uniqueKeys
    .map(key => {
      const value = testData[key] || `your_${key}`
      return `${key}=${encodeURIComponent(value)}`
    })
    .join('&')
  
  return params ? `?${params}` : ''
}

/**
 * Save macros to localStorage
 */
export const saveMacros = (macros: Macro[], testData: MacroTestData): void => {
  try {
    const session: MacroSession = {
      macros,
      testData,
      savedAt: new Date().toISOString()
    }
    localStorage.setItem('macro_generator_session', JSON.stringify(session))
  } catch (error) {
    console.error('Failed to save macros:', error)
  }
}

/**
 * Load macros from localStorage
 */
export const loadMacros = (): MacroSession | null => {
  try {
    const saved = localStorage.getItem('macro_generator_session')
    if (saved) {
      return JSON.parse(saved)
    }
  } catch (error) {
    console.error('Failed to load macros:', error)
  }
  return null
}

/**
 * Clear saved macros from localStorage
 */
export const clearMacros = (): void => {
  try {
    localStorage.removeItem('macro_generator_session')
  } catch (error) {
    console.error('Failed to clear macros:', error)
  }
}

/**
 * Pre-built macro templates
 */
export const MACRO_TEMPLATES: MacroTemplate[] = [
  {
    id: 'personalized_greeting',
    name: 'Personalized Greeting',
    description: 'Greet customers by name with fallback',
    category: 'Greetings',
    macros: [
      {
        pattern: MacroPattern.CONDITIONAL_ELSE,
        key: 'firstname',
        conditionalContent: 'Hey {{firstname}}, ',
        elseContent: 'Hello, ',
        description: 'Personalized greeting with fallback'
      }
    ]
  },
  {
    id: 'order_confirmation',
    name: 'Order Confirmation',
    description: 'Display order confirmation number',
    category: 'Orders',
    macros: [
      {
        pattern: MacroPattern.SIMPLE,
        key: 'confirmationref',
        description: 'Order confirmation reference'
      }
    ]
  },
  {
    id: 'order_confirmation_complete',
    name: 'Complete Order Message',
    description: 'Full order confirmation with personalization',
    category: 'Orders',
    macros: [
      {
        pattern: MacroPattern.CONDITIONAL,
        key: 'firstname',
        conditionalContent: 'Thank you, {{firstname}}! ',
        description: 'Conditional thank you with name'
      },
      {
        pattern: MacroPattern.SIMPLE,
        key: 'confirmationref',
        description: 'Order number'
      }
    ]
  },
  {
    id: 'personalized_offers',
    name: 'Personalized Offer Intro',
    description: 'Introduce offers based on purchase amount',
    category: 'Offers',
    macros: [
      {
        pattern: MacroPattern.CONDITIONAL,
        key: 'firstname',
        conditionalContent: '{{firstname}}, ',
        description: 'Optional name'
      },
      {
        pattern: MacroPattern.FALLBACK,
        key: 'amount',
        fallbackValue: 'your purchase',
        description: 'Purchase amount with fallback'
      }
    ]
  },
  {
    id: 'payment_confirmation',
    name: 'Payment Confirmation',
    description: 'Confirm payment method used',
    category: 'Payments',
    macros: [
      {
        pattern: MacroPattern.CONDITIONAL,
        key: 'paymenttype',
        conditionalContent: 'Thanks for paying with {{paymenttype}}. ',
        description: 'Payment method acknowledgment'
      }
    ]
  },
  {
    id: 'location_based',
    name: 'Location Based Promotion',
    description: 'Display location-specific offers',
    category: 'Location',
    macros: [
      {
        pattern: MacroPattern.SIMPLE,
        key: 'zipcode',
        description: 'ZIP code'
      },
      {
        pattern: MacroPattern.SIMPLE,
        key: 'country',
        description: 'Country code'
      }
    ]
  },
  {
    id: 'loyalty_enrollment',
    name: 'Loyalty Program Welcome',
    description: 'Welcome message for new loyalty members',
    category: 'Loyalty',
    macros: [
      {
        pattern: MacroPattern.CONDITIONAL_ELSE,
        key: 'firstname',
        conditionalContent: 'Welcome, {{firstname}}! ',
        elseContent: 'Welcome! ',
        description: 'Personalized welcome'
      },
      {
        pattern: MacroPattern.SIMPLE,
        key: 'confirmationref',
        description: 'First order reference'
      }
    ]
  },
  {
    id: 'full_personalization',
    name: 'Full Personalization Example',
    description: 'Complete example with multiple macros',
    category: 'Examples',
    macros: [
      {
        pattern: MacroPattern.CONDITIONAL,
        key: 'firstname',
        conditionalContent: 'Hey {{firstname}}, ',
        description: 'Greeting'
      },
      {
        pattern: MacroPattern.SIMPLE,
        key: 'confirmationref',
        description: 'Order number'
      },
      {
        pattern: MacroPattern.FALLBACK,
        key: 'amount',
        fallbackValue: 'N/A',
        description: 'Order total'
      }
    ]
  },
  {
    id: 'conditional_with_fallback',
    name: 'Conditional with Fallback',
    description: 'Use fallback value inside conditional block',
    category: 'Combined Patterns',
    macros: [
      {
        pattern: MacroPattern.ADVANCED,
        key: 'firstname',
        customSyntax: "{{#if firstname}}Hey {{firstname|'friend'}}, {{/if}}your order is ready",
        description: 'Conditional greeting with fallback inside'
      }
    ]
  },
  {
    id: 'nested_personalization',
    name: 'Nested Personalization',
    description: 'Multiple macros in conditional branches',
    category: 'Combined Patterns',
    macros: [
      {
        pattern: MacroPattern.ADVANCED,
        key: 'firstname',
        customSyntax: "{{#if firstname}}{{firstname}}'s order {{confirmationref}}{{else}}Order {{confirmationref}}{{/if}} is confirmed",
        description: 'Nested keys in conditional'
      }
    ]
  },
  {
    id: 'complex_greeting',
    name: 'Complex Greeting',
    description: 'Full conditional with nested fallbacks',
    category: 'Combined Patterns',
    macros: [
      {
        pattern: MacroPattern.ADVANCED,
        key: 'firstname',
        customSyntax: "{{#if firstname}}Hey {{firstname}}, {{else}}Hello, {{/if}}your order totaling {{amount|'N/A'}} is ready",
        description: 'Conditional + fallback combination'
      }
    ]
  },
  {
    id: 'payment_with_conditional_fallback',
    name: 'Payment with Conditional & Fallback',
    description: 'Show payment type conditionally with amount fallback',
    category: 'Combined Patterns',
    macros: [
      {
        pattern: MacroPattern.ADVANCED,
        key: 'paymenttype',
        customSyntax: "{{#if paymenttype}}Thanks for paying with {{paymenttype}}! {{/if}}Your payment of {{amount|'$0.00'}} has been processed.",
        description: 'Payment confirmation with nested macros'
      }
    ]
  }
]

/**
 * Generate a unique ID for macros
 */
export const generateMacroId = (): string => {
  return `macro_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
}

/**
 * Get templates filtered by message context
 */
export const getTemplatesByMessageContext = (context: string | null): MacroTemplate[] => {
  if (!context) {
    return MACRO_TEMPLATES
  }

  // Map context to relevant template categories
  const contextCategoryMap: Record<string, string[]> = {
    HEADER_TEXT: ['Greetings', 'Orders', 'Offers', 'Examples', 'Combined Patterns'],
    LEAD_IN_TEXT: ['Orders', 'Payments', 'Examples', 'Combined Patterns'],
    TITLE_TEXT: ['Greetings', 'Offers', 'Loyalty', 'Examples']
  }

  const relevantCategories = contextCategoryMap[context] || []
  
  if (relevantCategories.length === 0) {
    return MACRO_TEMPLATES
  }

  return MACRO_TEMPLATES.filter(template => 
    relevantCategories.includes(template.category)
  )
}

/**
 * Build macro from wizard answers
 */
export const buildMacroFromWizard = (wizardState: any): Omit<Macro, 'id'> => {
  const { 
    dataKey, 
    missingDataIntent, 
    fallbackValue, 
    conditionalContent, 
    elseContent, 
    description, 
    customSyntax, 
    macroPattern,
    patternData 
  } = wizardState

  // If in advanced mode, use custom syntax
  if (wizardState.advancedMode && customSyntax) {
    return {
      pattern: MacroPattern.ADVANCED,
      key: extractKeysFromSyntax(customSyntax)[0] || dataKey,
      customSyntax,
      description
    }
  }

  // If macroPattern was selected and we have patternData, use the new structure
  if (macroPattern && patternData) {
    switch (macroPattern) {
      case MacroPattern.SIMPLE:
        const simpleData = patternData.simple
        return {
          pattern: MacroPattern.SIMPLE,
          key: simpleData?.dataKey || dataKey,
          description: simpleData?.description || description
        }

      case MacroPattern.FALLBACK:
        const fallbackData = patternData.fallback
        return {
          pattern: MacroPattern.FALLBACK,
          key: fallbackData?.dataKey || dataKey,
          fallbackValue: fallbackData?.fallbackValue || fallbackValue || 'Guest',
          description: fallbackData?.description || description
        }

      case MacroPattern.CONDITIONAL:
        const conditionalData = patternData.conditional
        return {
          pattern: MacroPattern.CONDITIONAL,
          key: conditionalData?.dataKey || dataKey,
          conditionalContent: conditionalData?.conditionalContent || conditionalContent || `Welcome back, {{${conditionalData?.dataKey || dataKey}}}!`,
          description: conditionalData?.description || description
        }

      case MacroPattern.CONDITIONAL_ELSE:
        const conditionalElseData = patternData.conditionalElse
        return {
          pattern: MacroPattern.CONDITIONAL_ELSE,
          key: conditionalElseData?.dataKey || dataKey,
          conditionalContent: conditionalElseData?.conditionalContent || conditionalContent || `Welcome back, {{${conditionalElseData?.dataKey || dataKey}}}!`,
          elseContent: conditionalElseData?.elseContent || elseContent || 'Welcome!',
          description: conditionalElseData?.description || description
        }

      case MacroPattern.ADVANCED:
        const advancedData = patternData.advanced
        let advancedSyntax = customSyntax
        
        // Use the advanced pattern data if available
        if (advancedData?.mode === 'custom' && advancedData.customSyntax) {
          advancedSyntax = advancedData.customSyntax
        } else if (advancedData?.dataKey || dataKey) {
          const key = advancedData?.dataKey || dataKey
          advancedSyntax = `{{#if ${key}}}{{${key}}}{{else}}Guest{{/if}}`
        }

        return {
          pattern: MacroPattern.ADVANCED,
          key: extractKeysFromSyntax(advancedSyntax)[0] || advancedData?.dataKey || dataKey,
          customSyntax: advancedSyntax,
          description: advancedData?.description || description
        }

      default:
        return {
          pattern: MacroPattern.SIMPLE,
          key: dataKey,
          description
        }
    }
  }

  // Fallback to legacy logic using macroPattern without patternData (for backward compatibility)
  if (macroPattern) {
    switch (macroPattern) {
      case MacroPattern.SIMPLE:
        return {
          pattern: MacroPattern.SIMPLE,
          key: dataKey,
          description
        }

      case MacroPattern.FALLBACK:
        return {
          pattern: MacroPattern.FALLBACK,
          key: dataKey,
          fallbackValue: fallbackValue || 'Guest',
          description
        }

      case MacroPattern.CONDITIONAL:
        return {
          pattern: MacroPattern.CONDITIONAL,
          key: dataKey,
          conditionalContent: conditionalContent || `Welcome back, {{${dataKey}}}!`,
          description
        }

      case MacroPattern.CONDITIONAL_ELSE:
        return {
          pattern: MacroPattern.CONDITIONAL_ELSE,
          key: dataKey,
          conditionalContent: conditionalContent || `Welcome back, {{${dataKey}}}!`,
          elseContent: elseContent || 'Welcome!',
          description
        }

      case MacroPattern.ADVANCED:
        return {
          pattern: MacroPattern.ADVANCED,
          key: dataKey,
          customSyntax: customSyntax || `{{#if ${dataKey}}}{{${dataKey}}}{{else}}Guest{{/if}}`,
          description
        }

      default:
        return {
          pattern: MacroPattern.SIMPLE,
          key: dataKey,
          description
        }
    }
  }

  // Final fallback to old logic using missingDataIntent (for Shopify users who skip pattern selection)
  switch (missingDataIntent) {
    case 'none':
      return {
        pattern: MacroPattern.SIMPLE,
        key: dataKey,
        description
      }

    case 'fallback':
      return {
        pattern: MacroPattern.FALLBACK,
        key: dataKey,
        fallbackValue: fallbackValue || '',
        description
      }

    case 'conditional':
      if (elseContent && elseContent.trim() !== '') {
        return {
          pattern: MacroPattern.CONDITIONAL_ELSE,
          key: dataKey,
          conditionalContent: conditionalContent || '',
          elseContent: elseContent || '',
          description
        }
      } else {
        return {
          pattern: MacroPattern.CONDITIONAL,
          key: dataKey,
          conditionalContent: conditionalContent || '',
          description
        }
      }

    default:
      return {
        pattern: MacroPattern.SIMPLE,
        key: dataKey,
        description
      }
  }
}

